import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Card } from "../../components/ui/card";
import {
  TrendingUp, TrendingDown, Minus, ArrowUpRight, Phone, Mail, MessageSquare,
  Eye, Users, Zap, Target, FileText, Wrench, MapPin, RefreshCw, Loader2,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

const PALETTE = ["#3DB9E8", "#1E3A5F", "#E30613", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 6) return "Bonsoir";
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n !== "number") return n;
  if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return n.toLocaleString("fr-FR");
}

function DeltaPill({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono">—</span>;
  const up = pct > 0, down = pct < 0;
  const cls = up ? "text-emerald-600 bg-emerald-50" : down ? "text-red-600 bg-red-50" : "text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]";
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function Sparkline({ data, color = "#3DB9E8", gradientId = "spark" }) {
  if (!data || data.length < 2) return <div className="h-10" />;
  return (
    <ResponsiveContainer width="100%" height={44}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.45} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.75} fill={`url(#${gradientId})`} isAnimationActive animationDuration={1200} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function KPI({ icon: Icon, label, kpi, color = "#3DB9E8", suffix = "" }) {
  if (!kpi) return null;
  return (
    <Card className="p-5 border border-[hsl(var(--border))] relative overflow-hidden group hover:border-[color:var(--brand-navy)]/40 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, color }}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--muted-foreground))] font-bold">{label}</div>
        </div>
        <DeltaPill pct={kpi.delta_pct} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{fmt(kpi.value)}{suffix}</div>
      </div>
      {kpi.series && kpi.series.length > 1 && (
        <div className="mt-3 -mx-1">
          <Sparkline data={kpi.series} color={color} gradientId={`g-${label.replace(/\s/g, "")}`} />
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard", { params: { days: d } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (!data) return (
    <div className="p-10 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
      <Loader2 className="w-4 h-4 animate-spin" /> Chargement du tableau de bord…
    </div>
  );

  const { kpis, content, recent_leads, sources, top_pages, leads_total, leads_new } = data;
  const firstName = user?.first_name || user?.name?.split(" ")[0] || "";
  const greeting = getTimeGreeting();

  // Daily combined series
  const series = (kpis.pageviews.series || []).map((d, i) => ({
    date: d.date,
    label: new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
    pageviews: d.value,
    devis: kpis.devis.series?.[i]?.value || 0,
    leads: kpis.leads.series?.[i]?.value || 0,
  }));

  const leadsStatusColor = {
    new: "bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)]",
    contacted: "bg-amber-100 text-amber-700",
    won: "bg-emerald-100 text-emerald-700",
    lost: "bg-red-100 text-red-700",
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-dashboard">
      {/* HEADER — pro, sans emoji */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">
            Tableau de bord · {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)] leading-tight">
            {greeting}{firstName ? <>, <span className="text-[color:var(--brand-sky)]">{firstName}</span></> : ""}.
          </h1>
          <p className="mt-2 text-[hsl(var(--muted-foreground))] text-sm max-w-2xl">
            Synthèse de performance sur les {days} derniers jours — trafic, conversion et pipeline commercial.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-1" data-testid="dashboard-period-selector">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`px-4 h-9 rounded-full text-sm font-semibold transition-all ${days === d ? "bg-[color:var(--brand-navy)] text-white shadow" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`} data-testid={`period-${d}`}>{d}j</button>
          ))}
          <button onClick={() => load(days)} className="ml-1 w-9 h-9 rounded-full flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]" title="Rafraîchir" data-testid="dashboard-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* KPI row — 4 cols on desktop */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI icon={Eye}          label="Pages vues"      kpi={kpis.pageviews}    color="#3DB9E8" />
        <KPI icon={Phone}        label="Clics téléphone" kpi={kpis.tel_clicks}   color="#10b981" />
        <KPI icon={Mail}         label="Clics email"     kpi={kpis.email_clicks} color="#f59e0b" />
        <KPI icon={MessageSquare} label="Devis soumis"   kpi={kpis.devis}        color="#E30613" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <KPI icon={Users}        label="Leads reçus"     kpi={kpis.leads}        color="#1E3A5F" />
        <KPI icon={Target}       label="Taux conversion" kpi={kpis.conversion_rate} color="#8b5cf6" suffix="%" />
        <KPI icon={Zap}          label="Clics liens courts" kpi={kpis.short_clicks} color="#ec4899" />
        <Card className="p-5 border border-[hsl(var(--border))] bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c] text-white relative overflow-hidden" data-testid="kpi-pipeline">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[color:var(--brand-sky)]/10 blur-2xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-2">Pipeline commercial</div>
            <div className="font-display text-3xl font-black tabular-nums">{leads_total}</div>
            <div className="mt-2 flex items-center gap-1.5 text-[11px]">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[color:var(--brand-sky)]/20 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--brand-sky)] animate-pulse" />
                {leads_new} nouveaux
              </span>
              <Link to="/admin/leads" className="inline-flex items-center gap-0.5 text-white/70 hover:text-white" data-testid="goto-leads">
                Voir <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {/* BIG CHART — trafic & conversions */}
      <Card className="p-5 md:p-6 border border-[hsl(var(--border))] mb-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-display font-bold text-[color:var(--brand-navy)] text-lg">Évolution du trafic et des conversions</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Superposition pages vues, devis soumis et leads enregistrés.</p>
          </div>
          <div className="flex items-center gap-4 text-[11px] font-semibold">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#3DB9E8]" />Pages vues</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E30613]" />Devis</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1E3A5F]" />Leads</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <defs>
                <linearGradient id="grad-pv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3DB9E8" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#3DB9E8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-dv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E30613" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#E30613" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-ld" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E3A5F" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#1E3A5F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12, boxShadow: "0 10px 30px -10px rgba(0,0,0,0.12)" }} />
              <Area type="monotone" dataKey="pageviews" name="Pages vues" stroke="#3DB9E8" strokeWidth={2.5} fill="url(#grad-pv)" isAnimationActive animationDuration={900} />
              <Area type="monotone" dataKey="devis" name="Devis" stroke="#E30613" strokeWidth={2.5} fill="url(#grad-dv)" isAnimationActive animationDuration={1100} />
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#1E3A5F" strokeWidth={2.5} fill="url(#grad-ld)" isAnimationActive animationDuration={1300} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* 3-col breakdown */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* Sources */}
        <Card className="p-5 border border-[hsl(var(--border))]">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Sources de trafic</h3>
          {sources?.length ? (
            <>
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={sources} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2} isAnimationActive animationDuration={900}>
                      {sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-3 space-y-1.5 text-xs">
                {sources.slice(0, 5).map((s, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{s.name || "direct"}</span>
                    <strong className="tabular-nums">{s.value}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : <div className="py-10 text-center text-xs text-[hsl(var(--muted-foreground))]">Pas encore de sources tracées.</div>}
        </Card>

        {/* Top pages */}
        <Card className="p-5 border border-[hsl(var(--border))]">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Pages les plus vues</h3>
          {top_pages?.length ? (
            <ul className="space-y-2.5">
              {top_pages.slice(0, 8).map((p, i) => {
                const max = top_pages[0]?.views || 1;
                return (
                  <li key={i}>
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="font-mono truncate max-w-[70%]">{p.path}</span>
                      <strong className="tabular-nums">{p.views}</strong>
                    </div>
                    <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] transition-all" style={{ width: `${(p.views / max) * 100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <div className="text-xs text-[hsl(var(--muted-foreground))]">Aucune vue sur la période.</div>}
        </Card>

        {/* Recent leads live feed */}
        <Card className="p-5 border border-[hsl(var(--border))]" data-testid="recent-leads-feed">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Derniers leads</h3>
            <Link to="/admin/leads" className="text-[11px] font-semibold text-[color:var(--brand-sky)] hover:underline inline-flex items-center gap-0.5">
              Pipeline <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          {recent_leads?.length ? (
            <ul className="space-y-2.5">
              {recent_leads.slice(0, 6).map((l) => (
                <li key={l.id} className="flex items-center gap-2.5 p-2 -mx-2 rounded-lg hover:bg-[hsl(var(--muted))]/50 transition">
                  <div className="w-8 h-8 rounded-full bg-[color:var(--brand-navy)] text-white flex items-center justify-center text-[11px] font-bold uppercase shrink-0">
                    {(l.name || "?").slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[color:var(--brand-navy)] truncate">{l.name}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 truncate">
                      <span className="font-mono">{l.phone}</span>
                      {l.zone && <><span>·</span><span>{l.zone}</span></>}
                    </div>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${leadsStatusColor[l.status] || "bg-[hsl(var(--muted))]"}`}>{l.status}</span>
                </li>
              ))}
            </ul>
          ) : <div className="py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Aucun lead sur la période.</div>}
        </Card>
      </div>

      {/* Content inventory */}
      <Card className="p-5 border border-[hsl(var(--border))]">
        <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Inventaire éditorial</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { Icon: Wrench,   label: "Services",     value: content.services,       to: "/admin/services" },
            { Icon: FileText, label: "Articles",     value: content.blog,           to: "/admin/blog" },
            { Icon: Eye,      label: "Réalisations", value: content.realizations,   to: "/admin/realisations" },
            { Icon: MapPin,   label: "Zones SEO",    value: content.zones,          to: "/admin/zones" },
            { Icon: Target,   label: "Mots-clés",    value: content.keywords,       to: "/admin/rank-tracker" },
            { Icon: TrendingUp, label: "Top 10",     value: content.keywords_top10, to: "/admin/rank-tracker" },
          ].map((c, i) => (
            <Link key={i} to={c.to} className="p-3 rounded-xl border border-[hsl(var(--border))] hover:border-[color:var(--brand-navy)]/40 hover:bg-[hsl(var(--muted))]/40 transition group">
              <c.Icon className="w-4 h-4 text-[color:var(--brand-sky)] mb-2" />
              <div className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))] font-bold">{c.label}</div>
              <div className="font-display text-2xl font-black text-[color:var(--brand-navy)] mt-0.5 tabular-nums">{c.value}</div>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
